import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  NgZone,
  computed,
  effect,
  inject,
  linkedSignal,
  output,
  signal,
  untracked,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  KENDO_BUTTON,
  KENDO_BUTTONGROUP,
} from '@progress/kendo-angular-buttons';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { KENDO_TABSTRIP, SelectEvent } from '@progress/kendo-angular-layout';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { ActionEventType } from '@maximizer/core/shared/ui/config';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  ActionsRealtimeService,
  ActionsStore,
} from '@maximizer/actions/data-access';
import {
  Action,
  ActionStatus,
  ActionType,
} from '@maximizer/actions/domain';
import { ActionCardComponent } from '../action-card/action-card.component';
import {
  ACTION_TYPE_META,
  actionTypeMeta,
} from '../action-card/action-card-type-meta';
import {
  ActionTypeMeta,
  ApproveEvent,
  DoneRow,
  RejectEvent,
} from '../models/action-card.model';

type InboxTab = 'review' | 'done';
type TypeFilter = ActionType | 'all';
type SortOrder = 'newest' | 'oldest';

const chipTypeOf = (type: ActionType): ActionType =>
  type === ActionType.EmailBatch ? ActionType.Email : type;

const TYPE_ORDER = Object.keys(ACTION_TYPE_META) as ActionType[];

@Component({
  selector: 'maximizer-actions',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    TranslatePipe,
    KENDO_BUTTON,
    KENDO_BUTTONGROUP,
    KENDO_GRID,
    KENDO_TABSTRIP,
    UI_KENDO,
    ActionCardComponent,
  ],
  styleUrls: ['./actions.component.scss'],
  templateUrl: './actions.component.html',
})
export class ActionsComponent extends EmbeddedComponent {
  @HostBinding('class') hostClasses = 'ai-ng';

  readonly approve = output<ApproveEvent>();
  readonly reject = output<RejectEvent>();

  protected readonly store = inject(ActionsStore);
  private readonly ngZone = inject(NgZone);
  private readonly realtime = inject(ActionsRealtimeService);

  protected readonly initialized = signal(false);
  // Back to To review and every type whenever something outside asks, such as
  // IQ Boost's link: the inbox may be open already on Done or under a filter.
  protected readonly tab = linkedSignal<number, InboxTab>({
    source: this.store.reviewRequests,
    computation: () => 'review',
  });
  protected readonly filter = linkedSignal<number, TypeFilter>({
    source: this.store.reviewRequests,
    computation: () => 'all',
  });
  protected readonly sortOrder = signal<SortOrder>('newest');

  private readonly openId = signal<string | null>(null);

  protected readonly filterTypes = computed<readonly ActionType[]>(() => {
    const present = new Set(
      this.store.actions().map((a) => chipTypeOf(a.type)),
    );
    return TYPE_ORDER.filter((type) => present.has(type));
  });

  protected readonly reviewCards = computed<readonly Action[]>(() =>
    this.store
      .pending()
      .filter((action) => this.matches(action))
      .sort(this.byDate((action) => action.createdAt)),
  );

  protected readonly doneRows = computed<DoneRow[]>(() =>
    this.store
      .done()
      .filter((action) => this.matches(action))
      .sort(this.byDate(decidedAt))
      .map((action) => ({
        action,
        meta: actionTypeMeta(action.type),
        ok: action.status === ActionStatus.Done,
        failed: action.status === ActionStatus.Failed,
      })),
  );

  constructor() {
    super(inject(ElementRef));

    effect(() => {
      const filter = this.filter();
      if (filter !== 'all' && !this.filterTypes().includes(filter)) {
        this.filter.set('all');
      }
    });

    effect(() => {
      const id = this.openId();
      const loading = this.store.loading();

      if (id && !loading) {
        untracked(() => this.store.expand(id));
      }
    });
  }

  override doAction(name: string, args?: unknown): void {
    this.ngZone.run(() => {
      if (name === ActionEventType.Initialize) {
        this.initialize();
      } else {
        super.doAction(name, args);
      }
    });
  }

  private initialize(): void {
    this.initialized.set(true);
    this.store.load();
    this.realtime.connect();
  }

  protected metaOf(type: ActionType): ActionTypeMeta {
    return actionTypeMeta(type);
  }

  protected tileClass(meta: ActionTypeMeta): string {
    return meta.external
      ? 'bg-inserv-10 text-inserv-140'
      : 'bg-neutral-10 text-neutral-90';
  }

  protected toggleSort(): void {
    this.sortOrder.update((order) =>
      order === 'newest' ? 'oldest' : 'newest',
    );
  }

  protected onTabSelect(event: SelectEvent): void {
    this.tab.set(event.index === 1 ? 'done' : 'review');
  }

  protected isOpen(id: string): boolean {
    return this.openId() === id;
  }

  protected setOpen(id: string, expanded: boolean): void {
    this.openId.set(expanded ? id : null);
  }

  protected onApprove(event: ApproveEvent): void {
    this.store.approve(event.id, event.payload);
    this.approve.emit(event);
  }

  protected onReject(event: RejectEvent): void {
    this.store.reject(event.id, event.reason);
    this.reject.emit(event);
  }

  private byDate(
    key: (action: Action) => string,
  ): (a: Action, b: Action) => number {
    const direction = this.sortOrder() === 'newest' ? -1 : 1;
    return (a, b) => direction * key(a).localeCompare(key(b));
  }

  private matches(action: Action): boolean {
    const filter = this.filter();
    return filter === 'all' || chipTypeOf(action.type) === filter;
  }
}

const decidedAt = (action: Action): string =>
  action.decision?.at ?? action.createdAt;
